import { filter } from 'lodash';
import { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Stack,
  Button,
  TableRow,
  TableBody,
  TableCell,
  Container,
  Typography,
  TableContainer,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Box,
  IconButton
} from '@mui/material';
import axios from 'axios';

import Iconify from '../components/Iconify';
import Page from '../components/Page';
import Scrollbar from '../components/Scrollbar';
import SearchNotFound from '../components/SearchNotFound';
import { HrconclaveListToolbar, HrconclaveListHead, HrconclaveMoreMenu } from '../sections/reports';

/* ---------------- TABLE HEAD ---------------- */

const TABLE_HEAD = [
  { id: 'title', label: 'Title', alignRight: false },
  { id: 'description', label: 'Description', alignRight: false },
  { id: 'objectiveList', label: 'Objectives', alignRight: false },
  { id: 'action', label: 'Action', alignRight: false }
];

/* ---------------- SORT ---------------- */

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) return -1;
  if (b[orderBy] > a[orderBy]) return 1;
  return 0;
}

function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function applySortFilter(array, comparator, query) {
  const stabilized = array.map((el, index) => [el, index]);

  stabilized.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });

  let data = stabilized.map((el) => el[0]);

  if (query) {
    data = filter(data, (item) =>
      item.title.toLowerCase().includes(query.toLowerCase()) ||
      item.description.toLowerCase().includes(query.toLowerCase())
    );
  }

  return data;
}

/* ---------------- COMPONENT ---------------- */

export default function Objective() {

  const [page, setPage] = useState(0);
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('title');
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [filterName, setFilterName] = useState('');

  const [objectives, setObjectives] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [openModal, setOpenModal] = useState(false);

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [objectiveList, setObjectiveList] = useState([]);
  const [newPoint, setNewPoint] = useState('');

  
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [selectedHrconclave, setSelectedHrconclave] = useState(null);

  useEffect(() => {
    fetchObjectives();
  }, []);

  const fetchObjectives = async () => {
    const res = await axios.get('https://cpccbe.org/api/objective');
    setObjectives(res.data);
  };

   const handleCloseViewModal = () => {
    setViewModalOpen(false);
    setSelectedHrconclave(null);
  };

  const handleOpenModal = (id = null) => {
    setEditingId(id);
    setTitle('');
    setDescription('');
    setObjectiveList([]);
    setNewPoint('');
    setOpenModal(true);

    if (id) {
      const obj = objectives.find(o => o._id === id);
      if (obj) {
        setTitle(obj.title);
        setDescription(obj.description);
        setObjectiveList(obj.objectiveList);
      }
    }
  };

  const handleCloseModal = () => setOpenModal(false);

  const handleAddPoint = () => {
    if (newPoint.trim()) {
      setObjectiveList([...objectiveList, newPoint]);
      setNewPoint('');
    }
  };

   const handleViewhrconclave = (hrconclave) => {
    setSelectedHrconclave(hrconclave);
    setViewModalOpen(true);
  };

  const handleRemovePoint = (index) => {
    const updated = [...objectiveList];
    updated.splice(index, 1);
    setObjectiveList(updated);
  };

  const handleCreate = async () => {
    await axios.post('https://cpccbe.org/api/objective', {
      title,
      description,
      objectiveList
    });
    fetchObjectives();
    handleCloseModal();
  };

  const handleUpdate = async () => {
    await axios.put(`https://cpccbe.org/api/objective/${editingId}`, {
      title,
      description,
      objectiveList
    });
    fetchObjectives();
    handleCloseModal();
  };

  const handleDelete = async (id) => {
    if (window.confirm('Delete this objective?')) {
      await axios.delete(`https://cpccbe.org/api/objective/${id}`);
      fetchObjectives();
    }
  };

  const filteredData = applySortFilter(objectives, getComparator(order, orderBy), filterName);
  const emptyRows = Math.max(0, (1 + page) * rowsPerPage - objectives.length);
  const isNotFound = filteredData.length === 0;

  return (
    <Page title="Objectives">
      <Container>

        <Stack direction="row" justifyContent="space-between" mb={5}>
          <Typography variant="h4">Objectives</Typography>
          <Button
            variant="contained"
            startIcon={<Iconify icon="eva:plus-fill" />}
            onClick={() => handleOpenModal()}
          >
            New Objective
          </Button>
        </Stack>

        <Card>

          <HrconclaveListToolbar
            filterName={filterName}
            onFilterName={(e) => setFilterName(e.target.value)}
          />

          <Scrollbar>
            <TableContainer>
              <Table>

                <HrconclaveListHead
                  order={order}
                  orderBy={orderBy}
                  headLabel={TABLE_HEAD}
                  rowCount={objectives.length}
                  onRequestSort={(e, p) => setOrderBy(p)}
                />

                <TableBody>
                  {filteredData
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map(row => (
                      <TableRow hover key={row._id}>
                        <TableCell padding="checkbox">
                                                    <IconButton aria-label="view" onClick={() => handleViewhrconclave(row)}>
                                                      <Iconify icon="eva:eye-fill" />
                                                    </IconButton>
                                                  </TableCell>

                        <TableCell>{row.title}</TableCell>

                        <TableCell>{row.description}</TableCell>

                        <TableCell>
                          {row.objectiveList.length && row.objectiveList.map((o, i) => (
                            <Typography key={i} variant="caption" display="block">
                               {o}
                            </Typography>
                          ))}
                        </TableCell>

                        <TableCell align="right">
                          <HrconclaveMoreMenu
                            onEdit={() => handleOpenModal(row._id)}
                            onDelete={() => handleDelete(row._id)}
                          />
                        </TableCell>

                      </TableRow>
                    ))}

                 
                </TableBody>

                {isNotFound && (
                  <TableBody>
                    <TableRow>
                      <TableCell colSpan={4} align="center">
                        <SearchNotFound searchQuery={filterName} />
                      </TableCell>
                    </TableRow>
                  </TableBody>
                )}

              </Table>
            </TableContainer>
          </Scrollbar>

          <TablePagination
            page={page}
            component="div"
            count={objectives.length}
            rowsPerPage={rowsPerPage}
            onPageChange={(e, p) => setPage(p)}
            rowsPerPageOptions={[5, 10, 25]}
            onRowsPerPageChange={(e) => setRowsPerPage(parseInt(e.target.value, 10))}
          />

        </Card>

        {/* -------- MODAL -------- */}

        <Dialog open={openModal} onClose={handleCloseModal} fullWidth>

          <DialogTitle>{editingId ? 'Edit Objective' : 'New Objective'}</DialogTitle>

          <DialogContent>

            <TextField
              label="Title"
              fullWidth
              margin="dense"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />

            <TextField
              label="Description"
              fullWidth
              multiline
              rows={3}
              margin="dense"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />

            <Box mt={2}>
              <TextField
                label="Add Objective Point"
                fullWidth
                value={newPoint}
                onChange={(e) => setNewPoint(e.target.value)}
              />
              <Button onClick={handleAddPoint} sx={{ mt: 1 }}>
                Add Point
              </Button>
            </Box>

            <Box mt={2}>
              {objectiveList.map((p, i) => (
                <Box key={i} display="flex" alignItems="center">
                  <Typography>{p}</Typography>
                  <IconButton onClick={() => handleRemovePoint(i)}>
                    <Iconify icon="eva:trash-2-outline" />
                  </IconButton>
                </Box>
              ))}
            </Box>

          </DialogContent>

          <DialogActions>
            <Button onClick={handleCloseModal}>Cancel</Button>
            {editingId
              ? <Button onClick={handleUpdate}>Update</Button>
              : <Button onClick={handleCreate}>Create</Button>}
          </DialogActions>

        </Dialog>

        <Dialog open={viewModalOpen} onClose={handleCloseViewModal}>
          <DialogTitle>Objective Details</DialogTitle>
          <DialogContent>
            {selectedHrconclave && (
      <>
        <TextField
          label="Title"
          value={selectedHrconclave.title}
          margin="dense"
          InputProps={{ readOnly: true }}
          fullWidth
        />

        <TextField
          label="Description"
          value={selectedHrconclave.description}
          margin="dense"
          multiline
          rows={3}
          InputProps={{ readOnly: true }}
          fullWidth
        />

        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2">Objectives:</Typography>

          {selectedHrconclave.objectiveList.map((obj, index) => (
            <Typography key={index} variant="body2" sx={{ ml: 1 }}>
              • {obj}
            </Typography>
          ))}
        </Box>
      </>
    )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseViewModal}>Close</Button>
          </DialogActions>
        </Dialog>

      </Container>
    </Page>
  );
}
